/**
 * Variant availability blur + auto-fallback (Dawn — variant-selects markup)
 * ---------------------------------------------------------------------------
 * - Blurs Size options that don't exist/aren't available for the selected
 *   Design (and blurs Design swatches that aren't available for the
 *   selected Size).
 * - If the currently selected combo becomes unavailable, auto-clicks the
 *   nearest available option so the customer is never stuck on sold-out.
 *
 * WHERE TO ADD:
 * 1. Save as: assets/variant-availability.js
 * 2. In snippets/product-variant-picker.liquid, add this line
 *    just before the closing </variant-selects> tag:
 *
 *    {{ 'variant-availability.js' | asset_url | script_tag }}
 *
 * 3. Add the CSS block (square-media.css) to assets/base.css — it now
 *    includes the .variant-option--unavailable blur style.
 *
 * No option name changes needed — this reads option names directly
 * from the product JSON (via data-product-handle), so it works
 * automatically as long as your options are called "Design" and "Size".
 * If they're named differently, edit the two constants below.
 */

(function () {
  const OPTION_DESIGN = "Design";
  const OPTION_SIZE = "Size";

  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("variant-selects").forEach(setupPicker);
  });

  async function setupPicker(root) {
    const handle = root.dataset.productHandle;
    if (!handle) return;

    let product;
    try {
      const res = await fetch(`/products/${handle}.js`);
      product = await res.json();
    } catch (e) {
      console.warn("variant-availability: could not fetch product JSON for", handle, e);
      return;
    }

    const optionNames = product.options; // e.g. ["Design", "Size"]
    const designIndex = optionNames.indexOf(OPTION_DESIGN);
    const sizeIndex = optionNames.indexOf(OPTION_SIZE);
    if (designIndex === -1 || sizeIndex === -1) {
      console.warn("variant-availability: Design/Size not found in product options for", handle);
      return;
    }

    const variants = product.variants;
    let isUpdating = false;

    const inputs = () => Array.from(root.querySelectorAll('input[type="radio"]'));

    function optionKeyFor(input) {
      return input.getAttribute("data-option-name");
    }

    function getSelected() {
      const selected = {};
      inputs().forEach((input) => {
        if (input.checked) selected[optionKeyFor(input)] = input.value;
      });
      return selected;
    }

    function variantFor(design, size) {
      return variants.find((v) => {
        const opts = [v.option1, v.option2, v.option3];
        return opts[designIndex] === design && opts[sizeIndex] === size;
      });
    }

    function labelFor(input) {
      // Swatch markup: label wraps the input. Button markup: label follows via [for].
      return input.closest("label") || root.querySelector(`label[for="${input.id}"]`);
    }

    function setUnavailable(input, isUnavailable) {
      const label = labelFor(input);
      if (label) label.classList.toggle("variant-option--unavailable", isUnavailable);
    }

    function refresh() {
      if (isUpdating) return;
      const selected = getSelected();

      inputs().forEach((input) => {
        const optName = optionKeyFor(input);
        if (optName === OPTION_SIZE && selected[OPTION_DESIGN]) {
          const match = variantFor(selected[OPTION_DESIGN], input.value);
          setUnavailable(input, !match || !match.available);
        }
        if (optName === OPTION_DESIGN && selected[OPTION_SIZE]) {
          const match = variantFor(input.value, selected[OPTION_SIZE]);
          setUnavailable(input, !match || !match.available);
        }
      });

      const current = variantFor(selected[OPTION_DESIGN], selected[OPTION_SIZE]);
      if (current && current.available) return;

      // Current combo unavailable -> jump to nearest available Size for this Design
      const options = variants.filter(
        (v) => [v.option1, v.option2, v.option3][designIndex] === selected[OPTION_DESIGN] && v.available
      );
      if (!options.length) return;

      const fallbackSize = [options[0].option1, options[0].option2, options[0].option3][sizeIndex];
      const target = inputs().find(
        (input) => optionKeyFor(input) === OPTION_SIZE && input.value === fallbackSize
      );
      if (target && !target.checked) {
        isUpdating = true;
        target.click();
        setTimeout(() => (isUpdating = false), 50);
      }
    }

    root.addEventListener("change", () => setTimeout(refresh, 0));
    refresh();
  }
})();